import { useContext } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { Typography } from "@mui/material";

import { scopesList } from "@aclymatepackages/subcategories";
import { litersToUsGallons } from "@aclymatepackages/converters";

import { fetchOurApi } from "../../../helpers/utils/apiCalls";
import { useAuth } from "../../../helpers/firebase";
import { analyticsTrack } from "../../../helpers/analytics";

import { SurveyContext } from "../survey-shape/SurveyContext";

dayjs.extend(utc);

const convertFuelVolumeToGallons = (fuelVolume, fuelVolumeUnit) => {
  if (fuelVolumeUnit === "liters") {
    return litersToUsGallons(fuelVolume);
  }
  return fuelVolume;
};

const clampFormStepToBounds = (currentStep, stepsLength) => {
  if (stepsLength <= 0) {
    return currentStep;
  }
  if (currentStep >= stepsLength) {
    return stepsLength - 1;
  }
  return currentStep;
};

const transactionsQuestions = [
  {
    title: "Business Travel",
    naics: 561599,
    tonsCo2ePerDollar: 0.000103,
    scopeThreeCategory: 6,
    description:
      "The business travel category includes any business-related travel outside of company vehicles, including air travel, for example. This information is important to provide so that all travel activities are accounted for in your full business emissions total.",
    questions: [
      { label: "Train tickets", naics: 485112, tonsCo2ePerDollar: 0.000499 },
      { label: "Flight Tickets", naics: 481111, tonsCo2ePerDollar: 0.000976 },
      { label: "Bus Tickets", naics: 485210, tonsCo2ePerDollar: 0.000499 },
      { label: "Car Rental", naics: 532111, tonsCo2ePerDollar: 0.000143 },
      {
        label: "Hotel + Restaurants + Catering",
        naics: 721110,
        tonsCo2ePerDollar: 0.000183
      }
    ]
  },
  {
    title: "Transportation/Freight",
    naics: 488999,
    tonsCo2ePerDollar: 0.000208,
    scopeThreeCategory: 4,
    description:
      "This category captures the inbound and outbound transportation of the products your company both buys and sells. This is important to include, because it's a contributor to your Scope 3 emissions, helping you and your buyer to get the most complete picture of your overall emissions.",
    questions: [
      { label: "Road Freight", naics: 488490, tonsCo2ePerDollar: 0.000208 },
      { label: "Air Freight", naics: 481112, tonsCo2ePerDollar: 0.000976 },
      { label: "Sea Freight", naics: 483113, tonsCo2ePerDollar: 0.000618 },
      { label: "Rail Freight", naics: 482111, tonsCo2ePerDollar: 0.000558 },
      {
        label: "Other Transport Services",
        helperText: "Warehousing, etc...",
        naics: 493190,
        tonsCo2ePerDollar: 0.000568
      }
    ]
  },
  {
    title: "Capital Goods",
    naics: 423830,
    tonsCo2ePerDollar: 0.000117,
    scopeThreeCategory: 2,
    description:
      "Capital goods are final products that have an extended life and are used by your company to manufacture a product, provide a service, or sell, store and deliver merchandise. Providing the most detailed information possible ensures that everything your company purchases for these reasons are accounted for in your emissions.",
    questions: [
      {
        label: "Furniture",
        helperText: "Chairs, tables, other office interior",
        naics: 423210,
        tonsCo2ePerDollar: 0.000147
      },
      {
        label: "Paper and packaging",
        helperText: "Cardboard, paper, post-its, etc...",
        naics: 561910,
        tonsCo2ePerDollar: 0.000121
      },
      {
        label: "Textiles",
        helperText: "Work clothes, fabrics, clothes resold in stores",
        naics: 314999,
        tonsCo2ePerDollar: 0.000263
      },
      {
        label: "Metal Products",
        helperText: "Steel beams, staplers or metals",
        naics: 326199,
        tonsCo2ePerDollar: 0.000403
      },
      {
        label: "Chemical and Pharmaceuticals",
        helperText: "Paints, chemicals, medicines",
        naics: 325412,
        tonsCo2ePerDollar: 0.000107
      },
      {
        label: "Other General Products",
        naics: 488999,
        helperText: "Food products, misc. inventory",
        tonsCo2ePerDollar: 0.000208
      },
      {
        label: "Book, movies and related services",
        helperText: "Publishing, printing, and reproduction of recorded media",
        naics: 451211,
        tonsCo2ePerDollar: 0.000163
      },
      {
        label: "Phones, televisions and comms equipment",
        helperText: "Smartphones, monitors, radios",
        naics: 517911,
        tonsCo2ePerDollar: 0.000125
      },
      {
        label: "Computers and office machinery",
        helperText: "Printers, computers or label makers",
        naics: 811212,
        tonsCo2ePerDollar: 0.00009
      },
      {
        label: "Purchased Vehicles",
        helperText: "Motor vehicles, trailers, boats, etc...",
        naics: 336211,
        tonsCo2ePerDollar: 0.000247
      },
      {
        label: "Other Machinery Tools and Equipment",
        helperText: "Drills, large machinery, heaters or processors",
        naics: 333249,
        tonsCo2ePerDollar: 0.00021
      }
    ]
  },
  {
    title: "Services",
    naics: 561499,
    tonsCo2ePerDollar: 0.000118,
    scopeThreeCategory: 1,
    description:
      "Similar to capital goods, it's important to track the services your business uses such as legal fees, software purchases, financial dealings, etc., to get the most comprehensive measurement of your emissions.",
    questions: [
      {
        label: "Legal, Accounting and Management Consulting",
        helperText: "Consultants, lawyers, accountants",
        naics: 541199,
        tonsCo2ePerDollar: 0.000054
      },
      {
        label: "Software, hosting, computer programming, etc...",
        helperText:
          "IT consultants, outsourced programming, SaaS subscriptions, data hosting services, software, digital advertising",
        naics: 511210,
        tonsCo2ePerDollar: 0.000097
      },
      {
        label: "Insurance and Pension Funding",
        helperText: "Insurance Costs",
        naics: 524292,
        tonsCo2ePerDollar: 0.000034
      },
      {
        label: "Financial Intermediation",
        helperText: "Banking fees and charges",
        naics: 523999,
        tonsCo2ePerDollar: 0.000062
      },
      {
        label: "Construction and Maintenance",
        naics: 236220,
        tonsCo2ePerDollar: 0.000256
      }
    ]
  }
];

const extractScopeTons = (scopeTonsArray, scope) => {
  const scopeData = scopeTonsArray[scope - 1];
  if (!scopeData) {
    return 0;
  }
  const { tons } = scopeData;
  return tons || 0;
};

const DateRangeText = () => {
  const { selectedYear } = useContext(SurveyContext);
  return (
    <Typography variant="overline" color="textSecondary">
      From 1/1/{selectedYear} to 12/31/{selectedYear}
    </Typography>
  );
};

const useOnSurveyComplete = () => {
  const { vendorId, selectedYear } = useContext(SurveyContext);
  const [user] = useAuth();

  return async ({ totalTons, completenessPct, hotspots } = {}) => {
    const result = await fetchOurApi({
      user,
      path: "/vendors/on-explorer-survey-submit",
      method: "POST",
      data: { vendorId, surveyYear: selectedYear }
    });
    analyticsTrack("survey_completed", {
      surveyType: "emissions",
      year: Number(selectedYear),
      totalTons: totalTons ?? null,
      completenessPct: completenessPct ?? null,
      hotspots: Array.isArray(hotspots) ? hotspots : [],
      timestamp: Date.now()
    });
    return result;
  };
};

const buildScopeTonsFromEmissions = (emissions, surveyYear) => {
  if (!emissions || !emissions.length) {
    return [];
  }

  const surveyYearEmissions = emissions.filter(
    ({ date, source }) =>
      dayjs.utc(date).year() === Number(surveyYear) &&
      source === "vendor-survey"
  );

  const scopes = [1, 2, 3];

  return scopes.map((scope) => {
    const match = surveyYearEmissions.find(
      (emission) => emission.scope === scope
    );
    if (!match) {
      return { scope, tons: "", unknown: false };
    }
    return {
      scope,
      tons: match.volume,
      unknown: false
    };
  });
};

const filterVendorSurveyTransactions = (transactions) => {
  if (!Array.isArray(transactions)) {
    return [];
  }
  return transactions.filter(({ source }) => source === "vendor-survey");
};

// `date` is an arbitrary-day UTC-anchored instant (Firestore Timestamp or
// Date), not a local-day string -- dayjs.utc() keeps a Jan-1-UTC-midnight
// transaction attributed to the correct year in a timezone behind UTC (e.g.
// America/Denver), where bare dayjs() would roll it back a day into the
// prior year. See PRs #2027/#2029/#2083.
const filterTransactionsForSurveyYear = (transactions, surveyYear) => {
  if (!Array.isArray(transactions)) {
    return [];
  }
  return transactions.filter(({ date }) => {
    const dateValue = date?.toDate ? date.toDate() : date;
    return dayjs.utc(dateValue).year() === Number(surveyYear);
  });
};

const formatTransactionsForSurvey = (transactions) => {
  if (!Array.isArray(transactions) || !transactions.length) {
    return transactionsQuestions.map(() => ({}));
  }

  return transactionsQuestions.map(({ scopeThreeCategory }) => {
    const matchingTransactions = transactions.filter(
      (transaction) => transaction.scopeThreeCategory === scopeThreeCategory
    );

    return Object.fromEntries(
      matchingTransactions.map(({ emissionData, value }) => [
        emissionData?.naicsCode,
        value
      ])
    );
  });
};

const extractVehicleFuelData = (transactions) => {
  if (!Array.isArray(transactions)) {
    return {};
  }

  const dbFuelTransaction = transactions.find(
    ({ subcategory }) => subcategory === "fuel"
  );
  if (!dbFuelTransaction) {
    return {};
  }
  const { billGallons, fuelCost } = dbFuelTransaction;
  return {
    fuelVolume: billGallons,
    fuelDollars: fuelCost
  };
};

const SURVEY_FIELD_IDS = {
  yearSelector: "survey:year",
  scopeTons: "scope-tons:all",
  industry: "vendor:industry",
  revenue: "vendor:revenueYears",
  employees: "vendor:employeeCount",
  isRemote: "vendor:isRemote",
  address: "vendor:address",
  hasNoCompanyVehicles: "vendor:hasNoCompanyVehicles",
  facility: (idx) => `facility:${idx}`,
  vehicleFuel: "vehicle-fuel:all",
  transactions: (scopeThreeCategory) => `transactions:${scopeThreeCategory}`
};

const coerceConversationIdToString = (rawId) => {
  if (rawId === null || rawId === undefined || rawId === "") {
    return null;
  }
  return String(rawId);
};

const readSurveyProgressFromVendorSurveys = (vendorSurveys, year) => {
  if (!Array.isArray(vendorSurveys) || !year) {
    return {
      skippedFields: [],
      unknownFields: [],
      conversationId: null
    };
  }
  const entry = vendorSurveys.find(
    ({ year: entryYear }) => Number(entryYear) === Number(year)
  );
  if (!entry) {
    return {
      skippedFields: [],
      unknownFields: [],
      conversationId: null
    };
  }
  return {
    skippedFields: entry.skippedFields || [],
    unknownFields: entry.unknownFields || [],
    conversationId: coerceConversationIdToString(entry.conversationId)
  };
};

const buildVendorSurveysWithProgress = (vendorSurveys, year, patch) => {
  const numericYear = Number(year);
  const existing = Array.isArray(vendorSurveys) ? vendorSurveys : [];
  const hasEntry = existing.some(
    ({ year: entryYear }) => Number(entryYear) === numericYear
  );
  const updatedAt = Date.now();
  if (!hasEntry) {
    return [...existing, { year: numericYear, ...patch, updatedAt }];
  }
  return existing.map((entry) =>
    Number(entry.year) === numericYear
      ? { ...entry, ...patch, updatedAt }
      : entry
  );
};

const addUniqueFieldId = (existing, fieldId) =>
  existing.includes(fieldId) ? existing : [...existing, fieldId];

const removeFieldId = (existing, fieldId) =>
  existing.filter((id) => id !== fieldId);

const hasSubmittedSurveyForYear = (vendorSurveys, year) => {
  if (!Array.isArray(vendorSurveys) || !year) {
    return false;
  }
  return vendorSurveys.some(
    (entry) =>
      Number(entry?.year) === Number(year) &&
      entry?.tonsCo2ePerDollar !== undefined
  );
};

export {
  transactionsQuestions,
  scopesList,
  extractScopeTons,
  DateRangeText,
  useOnSurveyComplete,
  buildScopeTonsFromEmissions,
  filterVendorSurveyTransactions,
  filterTransactionsForSurveyYear,
  formatTransactionsForSurvey,
  extractVehicleFuelData,
  SURVEY_FIELD_IDS,
  readSurveyProgressFromVendorSurveys,
  buildVendorSurveysWithProgress,
  addUniqueFieldId,
  removeFieldId,
  convertFuelVolumeToGallons,
  clampFormStepToBounds,
  hasSubmittedSurveyForYear
};
